import React, { useState, useEffect, useRef } from 'react';
import { VscTrash, VscEdit, VscCheck, VscClose, VscSettings, VscCloudDownload } from 'react-icons/vsc';
import { MdPrint } from 'react-icons/md';
import { BsFilePdf } from 'react-icons/bs';
import { getSwitchboards, createSwitchboard, updateSwitchboard, deleteSwitchboard } from '../../api/switchboards';
import { getBreakers, createBreaker, updateBreaker, deleteBreaker } from '../../api/breakers';
import { getItems } from '../../api/items';
import { getRooms } from '../../api/rooms';
import { buildSwitchboardPdf } from '../../utils/pdfUtils';
import { getPrinters, submitPrintJob } from '../../api/printJobs';
import { importLookup } from '../../api/importLookups';
import PortalDropdown from '../common/PortalDropdown';
import SearchSelect from '../common/SearchSelect';
import { useSort, sortRows, SortTh } from '../common/SortHeader';
import '../common/PrintButton.css';
import './Lookups.css';

const VOLTAGES = ['690VAC', '440VAC', '400VAC', '230VAC', '24VAC', '24VDC'];
const emptyBoard   = () => ({ name: '', tag: '', description: '', voltage: '', room: '', sections: '' });
const emptyBreaker = () => ({ tag: '', type: '', setting: '', section: '' });

// "A, B" ⇄ ['A', 'B']
const parseSections = (s) => [...new Set(String(s || '').split(/[,;/]/).map(x => x.trim()).filter(Boolean))];

const breakerRowNav = (e, trEl, { save, cancel, up, down }) => {
  const cells   = [...trEl.querySelectorAll('td[data-cell]')];
  const curCell = cells.find(td => td.contains(document.activeElement));
  const idx     = cells.indexOf(curCell);
  const field   = (i) => cells[i]?.querySelector('input, select');
  const goTo    = (i) => { if (i < 0 || i >= cells.length) return false; field(i)?.focus(); return true; };

  if (e.key === 'F2') {
    e.preventDefault();
    const el = e.target;
    if (el.type === 'number') { const v = el.value; el.value = ''; el.value = v; }
    else { try { const len = el.value?.length ?? 0; el.setSelectionRange(len, len); } catch {} }
    return;
  }
  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { if (e.target.tagName === 'SELECT') return; }
  if (e.key === 'ArrowUp')   { e.preventDefault(); up?.(idx); return; }
  if (e.key === 'ArrowDown') { e.preventDefault(); down?.(idx); return; }
  if (e.key === 'Enter')     { e.preventDefault(); if (down) down(idx); else save(); return; }
  if (e.key === 'Tab') {
    e.preventDefault();
    if (e.shiftKey) goTo(idx - 1); else if (!goTo(idx + 1)) save();
    return;
  }
  if (e.key === 'Escape') { e.preventDefault(); cancel(); return; }
  if (e.key === 'ArrowRight') {
    const el = document.activeElement;
    if (el && el.tagName === 'INPUT' && el.type !== 'number' && el.selectionStart !== null && el.selectionEnd < el.value.length) return;
    e.preventDefault(); field(idx + 1)?.focus(); return;
  }
  if (e.key === 'ArrowLeft') {
    const el = document.activeElement;
    if (el && el.tagName === 'INPUT' && el.type !== 'number' && el.selectionStart !== null && el.selectionStart > 0) return;
    e.preventDefault(); field(idx - 1)?.focus(); return;
  }
};

function BoardForm({ initial, onSave, onCancel, roomOpts }) {
  const [f, setF] = useState(initial || emptyBoard());
  const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));
  const handleKey = e => {
    if (e.key === 'Enter' && e.target.tagName !== 'SELECT') { e.preventDefault(); onSave(f); }
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };
  return (
    <div className="board-form" onKeyDown={handleKey}>
      <input placeholder="Name *" value={f.name} onChange={set('name')} autoFocus className="board-form-name" />
      <input placeholder="Tag"    value={f.tag}  onChange={set('tag')}  className="board-form-tag" />
      <input placeholder="Description" value={f.description} onChange={set('description')} className="board-form-desc" />
      <select value={f.voltage} onChange={set('voltage')} className="board-form-voltage">
        <option value="">— Voltage —</option>
        {VOLTAGES.map(v => <option key={v} value={v}>{v}</option>)}
      </select>
      <div className="board-form-room">
        <SearchSelect options={roomOpts} value={f.room} onChange={v => setF(p => ({ ...p, room: v }))} placeholder="— Room —" />
      </div>
      <input placeholder="Busbar sections, e.g. A, B" title="Busbar sections joined by bus-tie breakers — leave empty for one busbar"
        value={f.sections} onChange={set('sections')} className="board-form-sections" />
      <span className="board-form-actions">
        <button className="btn-icon" onClick={() => onSave(f)} title="Save"><VscCheck size={14} /></button>
        <button className="btn-icon" onClick={onCancel}        title="Cancel"><VscClose size={14} /></button>
      </span>
    </div>
  );
}

function SwitchboardsPanel({ project, role, onOpenTab, projects, isActive }) {
  const [boards, setBoards]                 = useState([]);
  const [breakers, setBreakers]             = useState([]);
  const [rooms, setRooms]                   = useState([]);
  const [selectedBoard, setSelected]        = useState(null);
  const [editingBoard, setEditingBoard]     = useState(null);
  const [newBoard, setNewBoard]             = useState(false);
  const [editingBreaker, setEditingBreaker] = useState(null);
  const [newBreaker, setNewBreaker]         = useState(emptyBreaker());
  const [brkSort, toggleBrkSort]            = useSort('tag');
  const brkSortInitRef = useRef(false);
  const [printOpen, setPrintOpen]             = useState(false);
  const [printerMenuOpen, setPrinterMenuOpen] = useState(false);
  const [printSelected, setPrintSelected]     = useState({});
  const [printSize, setPrintSize]             = useState(
    () => localStorage.getItem('print_size_switchboards') || 'a4'
  );
  const [printers, setPrinters] = useState(null);
  const [sending,  setSending]  = useState(false);
  const [importOpen, setImportOpen]       = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult]   = useState(null);
  const importRef = useRef(null);
  const undoRef      = useRef([]);
  const redoRef      = useRef([]);
  const beforeMapRef = useRef({});
  const undoRedoRef  = useRef(null);

  const changePrintSize = (s) => {
    setPrintSize(s);
    localStorage.setItem('print_size_switchboards', s);
  };
  const sortedBreakers = breakers;
  const breakerNewRef  = useRef(null);
  const breakerEditRef = useRef(null);
  const printRef       = useRef(null);

  const sel = e => e.target.select();

  const pushHistory = (id, before, after) => {
    if (!before || JSON.stringify(before) === JSON.stringify(after)) return;
    undoRef.current = [{ id, before, after }, ...undoRef.current].slice(0, 30);
    redoRef.current = [];
  };

  const canEdit  = ['admin','engineer','manager','foreman'].includes(role);
  const canPrint = ['admin','engineer','manager','foreman','team-leader'].includes(role);

  useEffect(() => {
    if (!project) return;
    getSwitchboards(project._id).then(r => setBoards(r.data)).catch(() => {});
    getRooms(project._id).then(r => setRooms(r.data)).catch(() => {});
  }, [project?._id]);

  useEffect(() => {
    if (!selectedBoard) { setBreakers([]); brkSortInitRef.current = false; return; }
    getBreakers(project._id, selectedBoard._id).then(r => {
      setBreakers(sortRows(r.data, brkSort, (b, k) => b[k] ?? null));
      brkSortInitRef.current = true;
    }).catch(() => {});
    setEditingBreaker(null);
    setNewBreaker(emptyBreaker());
  }, [selectedBoard?._id]);

  // Incoming / bus-tie labels are set elsewhere (Load Calculation → Supply setup) — refresh them when the tab comes back
  useEffect(() => {
    if (!isActive || !selectedBoard || editingBreaker) return;
    getBreakers(project._id, selectedBoard._id).then(r => {
      const byId = new Map(r.data.map(b => [b._id, b]));
      setBreakers(prev => prev.map(b => (byId.has(b._id) ? { ...b, role: byId.get(b._id).role, roleLabel: byId.get(b._id).roleLabel } : b)));
    }).catch(() => {});
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!brkSortInitRef.current) return;
    setBreakers(prev => sortRows(prev, brkSort, (b, k) => b[k] ?? null));
  }, [brkSort.key, brkSort.dir]);

  useEffect(() => {
    if (!isActive) return;
    const onKeyDown = async (e) => {
      if (!e.ctrlKey || (e.key !== 'z' && e.key !== 'y')) return;
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') && !el.closest('.lookup-split')) return;
      e.preventDefault();
      if (e.key === 'z') await undoRedoRef.current?.undo();
      else await undoRedoRef.current?.redo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isActive]);

  useEffect(() => {
    setPrintSelected(prev => {
      const next = { ...prev };
      boards.forEach(b => { if (!(b._id in next)) next[b._id] = true; });
      return next;
    });
  }, [boards]);

  useEffect(() => {
    if (!printOpen && !printerMenuOpen) return;
    const handler = (e) => {
      if (printRef.current && !printRef.current.contains(e.target)) {
        setPrintOpen(false);
        setPrinterMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [printOpen, printerMenuOpen]);

  // Fetch printers lazily when printer menu opens
  useEffect(() => {
    if (!printerMenuOpen || printers !== null) return;
    getPrinters().then(r => setPrinters(r.data)).catch(() => setPrinters([]));
  }, [printerMenuOpen]);

  const handleImport = async (srcProject) => {
    setImportOpen(false);
    setImportLoading(true);
    setImportResult(null);
    try {
      const res = await importLookup('switchboards', srcProject._id, project._id);
      const { imported, skipped, breakersImported } = res.data;
      let msg = `Imported ${imported} boards`;
      if (breakersImported) msg += ` + ${breakersImported} breakers`;
      if (skipped) msg += `, skipped ${skipped} duplicates`;
      setImportResult({ ok: true, msg });
      getSwitchboards(project._id).then(r => setBoards(r.data)).catch(() => {});
    } catch (e) {
      setImportResult({ ok: false, msg: e.response?.data?.msg || 'Import failed' });
    } finally {
      setImportLoading(false);
    }
  };

  const generatePdf = async () => {
    const selected = boards.filter(b => printSelected[b._id] !== false);
    if (!selected.length) return null;
    const [itemsRes, ...brkResults] = await Promise.all([
      getItems(project._id),
      ...selected.map(b => getBreakers(project._id, b._id)),
    ]);
    const boardsWithBreakers = selected.map((board, i) => ({ board, breakers: brkResults[i].data }));
    return buildSwitchboardPdf(project.name, boardsWithBreakers, itemsRes.data, printSize);
  };

  const handleOpenPdf = async () => {
    if (!onOpenTab) return;
    setPrinterMenuOpen(false);
    try {
      const blob = await generatePdf();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      onOpenTab(`print-pdf-${Date.now()}`, `Switchboards — ${project.name}`, { type: 'print-pdf', pdfUrl: url });
    } catch (err) { console.error('Switchboard PDF failed:', err); }
  };

  const handleSendToPrinter = async (printer) => {
    setPrinterMenuOpen(false);
    setSending(true);
    try {
      const blob = await generatePdf();
      if (!blob) return;
      const form = new FormData();
      form.append('pdf', blob, `Switchboards — ${project.name}.pdf`);
      form.append('printer', printer);
      await submitPrintJob(form);
    } catch (err) { console.error('Switchboard print failed:', err); }
    finally { setSending(false); }
  };

  const sections = selectedBoard?.sections || [];
  const sectionSelect = (value, onChange) => (
    <select value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">—</option>
      {sections.map(s => <option key={s} value={s}>{s}</option>)}
      {value && !sections.includes(value) && <option value={value}>{value} (removed)</option>}
    </select>
  );

  const roomOpts = rooms.map(r => ({ value: r._id, label: `${String(r.number).padStart(3,'0')} — ${r.text}` }));

  // ── Switchboard CRUD ──────────────────────────────────────
  const addBoard = async (f) => {
    if (!f.name.trim()) return;
    const res = await createSwitchboard({ project: project._id, name: f.name.trim(), tag: f.tag, description: f.description, voltage: f.voltage, room: f.room || undefined, sections: parseSections(f.sections) });
    setBoards(prev => [...prev, res.data]);
    setNewBoard(false);
  };

  const saveBoard = async (id, f) => {
    const res = await updateSwitchboard(id, { name: f.name, tag: f.tag, description: f.description, voltage: f.voltage, room: f.room || undefined, sections: parseSections(f.sections) });
    setBoards(prev => prev.map(b => b._id === id ? res.data : b));
    if (selectedBoard?._id === id) setSelected(res.data);
    setEditingBoard(null);
  };

  const removeBoard = async (id) => {
    if (!confirm('Delete this switchboard and all its breakers?')) return;
    await deleteSwitchboard(id);
    setBoards(prev => prev.filter(b => b._id !== id));
    if (selectedBoard?._id === id) { setSelected(null); setBreakers([]); }
  };

  // ── Breaker CRUD ──────────────────────────────────────────
  const focusBreakerEdit = (colIdx = 0) =>
    setTimeout(() => {
      const cells = [...(breakerEditRef.current?.querySelectorAll('td[data-cell]') || [])];
      (cells[colIdx] ?? cells[0])?.querySelector('input, select')?.focus();
    }, 0);

  const startEditBreaker = (brk, colIdx = 0) => {
    beforeMapRef.current[brk._id] = { tag: brk.tag, type: brk.type || '', setting: brk.setting || '', section: brk.section || '' };
    setEditingBreaker({ _id: brk._id, tag: brk.tag, type: brk.type || '', setting: brk.setting || '', section: brk.section || '' });
    setTimeout(() => {
      if (!breakerEditRef.current) return;
      const tds = [...breakerEditRef.current.querySelectorAll('td')];
      (tds[colIdx]?.querySelector('input, select') ?? breakerEditRef.current.querySelector('td[data-cell] input'))?.focus();
    }, 0);
  };

  const cancelEditBreaker = (brkId) => {
    delete beforeMapRef.current[brkId];
    setEditingBreaker(null);
    if (brkId) setTimeout(() => document.querySelector(`tr[data-brk-id="${brkId}"]`)?.focus(), 0);
  };

  const flushBreaker = async (brk) => {
    if (!brk) return;
    try {
      const res = await updateBreaker(brk._id, { tag: brk.tag, type: brk.type, setting: brk.setting, section: brk.section });
      // The function label (incoming / bus tie) is only computed by the list endpoint
      setBreakers(prev => prev.map(b => b._id === brk._id ? { ...res.data, role: b.role, roleLabel: b.roleLabel } : b));
    } catch {}
  };

  const commitBreaker = async () => {
    if (!editingBreaker) return;
    const { _id, ...fields } = editingBreaker;
    pushHistory(_id, beforeMapRef.current[_id], fields);
    delete beforeMapRef.current[_id];
    await flushBreaker(editingBreaker);
    setEditingBreaker(null);
    setTimeout(() => document.querySelector(`tr[data-brk-id="${_id}"]`)?.focus(), 0);
  };

  const saveNewBreaker = async () => {
    if (!newBreaker.tag.trim() || !selectedBoard) return;
    const res = await createBreaker({ project: project._id, switchboard: selectedBoard._id, ...newBreaker });
    setBreakers(prev => [...prev, res.data]);
    setNewBreaker(emptyBreaker());
    setTimeout(() => breakerNewRef.current?.querySelector('input')?.focus(), 0);
  };

  const removeBreaker = async (id) => {
    if (!confirm('Delete this breaker?')) return;
    await deleteBreaker(id);
    setBreakers(prev => prev.filter(b => b._id !== id));
    if (editingBreaker?._id === id) setEditingBreaker(null);
  };

  const handleBreakerNav = (dir, currentId, colIdx = 0) => {
    const isNew = currentId === '_new';
    if (!isNew) {
      if (editingBreaker) {
        const { _id, ...fields } = editingBreaker;
        pushHistory(_id, beforeMapRef.current[_id], fields);
        delete beforeMapRef.current[_id];
      }
      flushBreaker(editingBreaker);
    } else saveNewBreaker();

    if (isNew) {
      if (dir === 'up' && breakers.length > 0) startEditBreaker(breakers[sortedBreakers.length - 1], colIdx);
      return;
    }
    const idx = sortedBreakers.findIndex(b => b._id === currentId);
    if (dir === 'up') {
      if (idx > 0) startEditBreaker(sortedBreakers[idx - 1], colIdx);
      // else: already at top — stay
    } else {
      if (idx < sortedBreakers.length - 1) startEditBreaker(sortedBreakers[idx + 1], colIdx);
      else {
        setEditingBreaker(null);
        setTimeout(() => {
          const cells = [...(breakerNewRef.current?.querySelectorAll('td[data-cell]') || [])];
          ((cells[colIdx] ?? cells[0])?.querySelector('input, select'))?.focus();
        }, 0);
      }
    }
  };

  undoRedoRef.current = {
    undo: async () => {
      if (!undoRef.current.length) return;
      const [entry, ...rest] = undoRef.current;
      undoRef.current = rest;
      redoRef.current = [entry, ...redoRef.current].slice(0, 30);
      if (editingBreaker?._id === entry.id) setEditingBreaker(null);
      await flushBreaker({ _id: entry.id, ...entry.before });
    },
    redo: async () => {
      if (!redoRef.current.length) return;
      const [entry, ...rest] = redoRef.current;
      redoRef.current = rest;
      undoRef.current = [entry, ...undoRef.current].slice(0, 30);
      if (editingBreaker?._id === entry.id) setEditingBreaker(null);
      await flushBreaker({ _id: entry.id, ...entry.after });
    },
  };

  if (!project) return <div className="empty-state">Select a project first.</div>;

  return (
    <div className="lookup-split">
      {/* ── Switchboard list ── */}
      <div className="lookup-left">
        <div className="lookup-header">
          <span className="lookup-title">SWITCHBOARDS</span>
          {['admin','engineer','manager'].includes(role) && (
            <div className="import-btn-wrap" ref={importRef}>
              <button
                className="btn-icon"
                title="Import from another project"
                disabled={importLoading}
                onClick={() => setImportOpen(v => !v)}
              >
                <VscCloudDownload size={15} />
              </button>
              <PortalDropdown anchor={importRef} open={importOpen} onClose={() => setImportOpen(false)} className="import-dropdown">
                <div className="import-dropdown-title">Import from project</div>
                {(projects || []).filter(p => p._id !== project._id).length === 0 && (
                  <p className="import-dropdown-muted">No other projects</p>
                )}
                {(projects || []).filter(p => p._id !== project._id).map(p => (
                  <button key={p._id} className="import-dropdown-item" onClick={() => handleImport(p)}>
                    {p.name}
                  </button>
                ))}
              </PortalDropdown>
            </div>
          )}
          {importResult && (
            <span className={`import-result${importResult.ok ? '' : ' import-result-error'}`} style={{ fontSize: 10, marginLeft: 4 }}>
              {importResult.msg}
            </span>
          )}
        </div>
        <div className="lookup-list">
          {boards.map(b => (
            <div key={b._id} className={`lookup-item${selectedBoard?._id === b._id ? ' selected' : ''}`}
              onClick={() => setSelected(b)}>
              {editingBoard === b._id ? (
                <BoardForm
                  initial={{ name: b.name, tag: b.tag || '', description: b.description || '', voltage: b.voltage || '', room: b.room?._id || '', sections: (b.sections || []).join(', ') }}
                  roomOpts={roomOpts}
                  onSave={f => saveBoard(b._id, f)}
                  onCancel={() => setEditingBoard(null)} />
              ) : (
                <>
                  <div className="lookup-item-main">
                    <span className="lookup-item-name">{b.name}</span>
                    {b.tag     && <span className="lookup-item-sub">{b.tag}</span>}
                    {b.voltage && <span className="lookup-item-sub">{b.voltage}</span>}
                    {b.room    && <span className="lookup-item-sub">Rm {String(b.room.number).padStart(3,'0')}</span>}
                    {b.sections?.length > 0 && <span className="lookup-item-sub">Sections {b.sections.join('/')}</span>}
                  </div>
                  {b.description && <div className="lookup-item-desc">{b.description}</div>}
                  {canEdit && (
                    <span className="lookup-item-actions">
                      <button className="btn-icon" onClick={e => { e.stopPropagation(); setEditingBoard(b._id); }}><VscEdit size={13} /></button>
                      <button className="btn-icon" onClick={e => { e.stopPropagation(); removeBoard(b._id); }}><VscTrash size={13} /></button>
                    </span>
                  )}
                </>
              )}
            </div>
          ))}

          {/* Always-present new board form at the bottom */}
          {canEdit && (
            newBoard ? (
              <div className="lookup-item">
                <BoardForm roomOpts={roomOpts} onSave={addBoard} onCancel={() => setNewBoard(false)} />
              </div>
            ) : (
              <div className="lookup-item lookup-item-new" onClick={() => setNewBoard(true)}>
                <span className="lookup-item-new-label">+ New switchboard…</span>
              </div>
            )
          )}
          {boards.length === 0 && !newBoard && !canEdit && (
            <div className="lookup-empty">No switchboards yet.</div>
          )}
        </div>
      </div>

      {/* ── Breaker table ── */}
      <div className="lookup-right">
        <div className="lookup-header">
          <span className="lookup-title">{selectedBoard ? `BREAKERS — ${selectedBoard.name}` : 'BREAKERS'}</span>
          {boards.length > 0 && onOpenTab && canPrint && (
            <div className="print-btn-wrap" ref={printRef} style={{ marginLeft: 'auto' }}>
              <button className="btn-icon print-col-btn" title="Select switchboards"
                onClick={() => { setPrinterMenuOpen(false); setPrintOpen(v => !v); }}>
                <VscSettings size={14} />
              </button>
              <button className="btn-icon" title="Print" disabled={sending}
                onClick={() => { setPrintOpen(false); setPrinterMenuOpen(v => !v); }}>
                <MdPrint size={15} />
              </button>

              {/* Board selection panel */}
              {printOpen && (
                <div className="print-settings-panel">
                  <div className="print-size-row">
                    <button className={`print-size-btn${printSize === 'a4' ? ' active' : ''}`} onClick={() => changePrintSize('a4')}>A4</button>
                    <button className={`print-size-btn${printSize === 'a3' ? ' active' : ''}`} onClick={() => changePrintSize('a3')}>A3</button>
                  </div>
                  <div className="print-settings-title">Switchboards to print</div>
                  <div className="print-settings-list">
                    {boards.map(b => (
                      <label key={b._id} className="print-settings-item">
                        <input
                          type="checkbox"
                          checked={printSelected[b._id] !== false}
                          onChange={() => setPrintSelected(prev => ({ ...prev, [b._id]: !prev[b._id] }))}
                        />
                        {b.name}{b.voltage ? ` — ${b.voltage}` : ''}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Printer destination menu */}
              {printerMenuOpen && (
                <div className="print-settings-panel">
                  <button className="print-menu-item" onClick={handleOpenPdf}>
                    <BsFilePdf size={13} /> Open as PDF
                  </button>
                  {printers === null && <p className="print-menu-muted">Loading printers…</p>}
                  {printers !== null && printers.length > 0 && (
                    <>
                      <div className="print-menu-sep" />
                      {printers.map(p => (
                        <button key={p.name} className="print-menu-item" onClick={() => handleSendToPrinter(p.name)}>
                          <MdPrint size={13} /> {p.displayName || p.name}
                        </button>
                      ))}
                    </>
                  )}
                  {printers !== null && printers.length === 0 && (
                    <p className="print-menu-muted">No printers available</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {!selectedBoard ? (
          <div className="lookup-empty">Select a switchboard to view its breakers.</div>
        ) : (
          <div className="lookup-list">
            <table className="data-table breaker-table">
              <thead>
                <tr>
                  <SortTh sortKey="tag"     sort={brkSort} onSort={toggleBrkSort}>Tag</SortTh>
                  <SortTh sortKey="type"    sort={brkSort} onSort={toggleBrkSort}>Type</SortTh>
                  <SortTh sortKey="setting" sort={brkSort} onSort={toggleBrkSort}>Setting</SortTh>
                  {sections.length > 0 && <SortTh sortKey="section" sort={brkSort} onSort={toggleBrkSort}>Section</SortTh>}
                  {canEdit && <th style={{ width: 36 }}></th>}
                </tr>
              </thead>
              <tbody>
                {sortedBreakers.map(b => {
                  if (editingBreaker?._id === b._id) {
                    return (
                      <tr key={b._id} ref={breakerEditRef} className="row-editing"
                        onKeyDown={e => breakerRowNav(e, e.currentTarget, {
                          save:   commitBreaker,
                          cancel: () => cancelEditBreaker(b._id),
                          up:     (ci) => handleBreakerNav('up',   b._id, ci),
                          down:   (ci) => handleBreakerNav('down', b._id, ci),
                        })}>
                        <td data-cell><input value={editingBreaker.tag}     onChange={e => setEditingBreaker(p => ({ ...p, tag:     e.target.value }))} onFocus={sel} /></td>
                        <td data-cell><input value={editingBreaker.type}    onChange={e => setEditingBreaker(p => ({ ...p, type:    e.target.value }))} onFocus={sel} /></td>
                        <td data-cell><input value={editingBreaker.setting} onChange={e => setEditingBreaker(p => ({ ...p, setting: e.target.value }))} onFocus={sel} /></td>
                        {sections.length > 0 && <td data-cell>{sectionSelect(editingBreaker.section, v => setEditingBreaker(p => ({ ...p, section: v })))}</td>}
                        {canEdit && (
                          <td className="cell-actions">
                            <button className="btn-icon btn-icon-danger" title="Delete" onClick={() => removeBreaker(b._id)}>
                              <VscTrash size={13} />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  }
                  return (
                    <tr key={b._id}
                      data-brk-id={b._id}
                      tabIndex={canEdit ? -1 : undefined}
                      className={canEdit ? 'data-row-click' : ''}
                      onClick={e => {
                        if (!canEdit) return;
                        const tds = [...e.currentTarget.querySelectorAll('td')];
                        startEditBreaker(b, Math.max(0, tds.indexOf(e.target.closest('td'))));
                      }}
                      onKeyDown={!canEdit ? undefined : e => {
                        if (e.key === 'Escape') { e.preventDefault(); return; }
                        if (e.key === 'Enter' || e.key === 'F2') {
                          e.preventDefault(); startEditBreaker(b, 0);
                        } else if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          const idx = sortedBreakers.findIndex(x => x._id === b._id);
                          if (idx < sortedBreakers.length - 1)
                            document.querySelector(`tr[data-brk-id="${sortedBreakers[idx+1]._id}"]`)?.focus();
                          else
                            breakerNewRef.current?.querySelector('input')?.focus();
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          const idx = sortedBreakers.findIndex(x => x._id === b._id);
                          if (idx > 0)
                            document.querySelector(`tr[data-brk-id="${sortedBreakers[idx-1]._id}"]`)?.focus();
                        } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                          e.preventDefault(); startEditBreaker(b, 0);
                        }
                      }}>
                      <td>{b.tag}{b.roleLabel && <span className="brk-role">{b.roleLabel}</span>}</td>
                      <td>{b.type || '—'}</td>
                      <td>{b.setting || '—'}</td>
                      {sections.length > 0 && <td>{b.section || '—'}</td>}
                      {canEdit && <td />}
                    </tr>
                  );
                })}

                {/* Always-present new breaker row */}
                {canEdit && (
                  <tr ref={breakerNewRef} className="row-editing row-new"
                    onKeyDown={e => breakerRowNav(e, e.currentTarget, {
                      save:   saveNewBreaker,
                      cancel: () => setNewBreaker(emptyBreaker()),
                      up:     (ci) => handleBreakerNav('up', '_new', ci),
                      down:   null,
                    })}>
                    <td data-cell><input placeholder="Tag" value={newBreaker.tag}     onChange={e => setNewBreaker(p => ({ ...p, tag:     e.target.value }))} onFocus={sel} /></td>
                    <td data-cell><input placeholder="Type" value={newBreaker.type}   onChange={e => setNewBreaker(p => ({ ...p, type:    e.target.value }))} onFocus={sel} /></td>
                    <td data-cell><input placeholder="Setting" value={newBreaker.setting} onChange={e => setNewBreaker(p => ({ ...p, setting: e.target.value }))} onFocus={sel} /></td>
                    {sections.length > 0 && <td data-cell>{sectionSelect(newBreaker.section, v => setNewBreaker(p => ({ ...p, section: v })))}</td>}
                    <td />
                  </tr>
                )}

                {breakers.length === 0 && !canEdit && (
                  <tr><td colSpan={4 + (sections.length > 0 ? 1 : 0)} style={{ color: 'var(--text-muted)', padding: '12px 10px' }}>No breakers yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default SwitchboardsPanel;
